<template>
  <div v-if="!isNotFound">
    <aw-product-list
      :is-whole-loading-completed="isWholeLoadingCompleted"
      :is-more-loading-completed="isMoreLoadingCompleted"
      :item-count="itemCount"
      @load-more-product-items="loadMoreProductItems"
      @order-product-items="orderProductItems"
      @filter-product-items="filterProductItems"
    >
      <template #content>
        <aw-product-list-header v-if="itemCount" :product-counter="{ all: itemCount, loaded: products.length }">
          <template #listName>
            <lv-heading
              v-if="shortlist"
              tag="h1"
              level="2"
              version2
              no-gutter
            >
              {{ shortlist.title }}
            </lv-heading>
            <p v-if="shortlist" :class="$style.headline" v-html="shortlist.headline" />
          </template>
          <template #filters>
            <client-only>
              <template v-if="screenRange['mobile-max']">
                <aw-category-selection-filter
                  v-if="hasCategorySelectionFilter"
                  view="COMPACT"
                  show-title
                  @activate-filter="filterProductItems()"
                />
                <aw-subcategory-filter
                  view="FULL"
                  @activate-filter="filterProductItems()"
                />
              </template>
              <aw-product-list-order-by-and-mobile-filter-modal
                v-else
                is-sortable
                :is-filterable="false"
                controls-visible-status
              />
            </client-only>
          </template>
        </aw-product-list-header>
      </template>
    </aw-product-list>
    <aw-content-seo-text
      v-if="getCategoryFromTree && getCategoryFromTree.seoTextId"
      :content-id="getCategoryFromTree.seoTextId"
    />
  </div>
</template>

<script setup>
  import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
  import { navigateTo, refreshNuxtData, useAsyncData, useNuxtApp, useRoute } from 'nuxt/app';
  import { useAwRequestTracing } from '~~/common/composables/useAwRequestTracing';
  import { useCategorySelectionFilter } from '~~/common/composables/useCategorySelectionFilter';
  import { LvHeading } from '~~/common/components/loginet-vue-shop/index';
  import AwProductList from '~~/common/components/Page/ProductList/AwProductList';
  import AwProductListHeader from '~~/common/components/Page/ProductList/AwProductListHeader.vue';
  import { useIsReturningEnabled, useIsReturningFromProductPage } from '~~/common/composables/useProductListHistory';
  import { createScrollToProductId } from '~~/common/utils/productListHistoryScroll';
  import { MSG_SHORTLIST_NOT_FOUND, setCookieMsg } from '~~/common/server/globalMiddlewareConstants';

  import { useContentsStore } from '~~/common/stores/contents';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useCategoryStore } from '~~/common/stores/category';
  import { useProductsStore } from '~~/common/stores/products';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery';
  import { usePromotionStore } from '~~/shop/app/stores/promotion';
  import { useSeoStore } from '~~/common/stores/seo.js';
  import { SEO_MODULE_SHORTLIST } from '~~/common/config/SeoConfig';

  const AwSubcategoryFilter = defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwSubcategoryFilter.vue'));
  const AwCategorySelectionFilter = defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwCategorySelectionFilter.vue'));
  const AwContentSeoText = defineAsyncComponent(() => import('~~/shop/app/components/AwContentSeoText'));
  const AwProductListOrderByAndMobileFilterModal = defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwProductListOrderByAndMobileFilterModal'));

  const { $logger, $localePath } = useNuxtApp();
  const route = useRoute();
  const categoryStore = useCategoryStore();
  const productsStore = useProductsStore();
  const promotionStore = usePromotionStore();
  const contentsStore = useContentsStore();
  const deliveryStore = useDeliveryStore();
  const userInterfaceStore = useUserInterfaceStore();
  const seoStore = useSeoStore();

  const isReturningFromProductPage = useIsReturningFromProductPage();
  const isReturningEnabled = useIsReturningEnabled();

  const isWholeLoadingCompleted = ref(true);
  const isMoreLoadingCompleted = ref(true);

  // treeId / boutiqueId come straight from the route query, so they are
  // identical on server and client and don't need to round-trip through the
  // useAsyncData payload — they are just plain consts in setup scope.
  const treeId = parseInt(route.query.categoryId, 10) || null;
  const boutiqueId = parseInt(route.query.boutiqueId, 10) || null;

  // The cache key must include every input that changes the resolved payload.
  // boutiqueId / categoryId alter which products are fetched and the derived
  // treeId; leaving them out would let a navigation to the same list_name with
  // a different boutique/category return the previous (stale) cached result.
  const { data: shortlistResult } = await useAsyncData(`shortlist_${route.params.list_name}_${route.query.shType || 'slug'}_${boutiqueId ?? ''}_${treeId ?? ''}`, async () => {
    try {
      const shortlistReq = promotionStore.fetchShortlists({
        identifier: route.params.list_name,
        type: route.query.shType || 'slug',
      }).then(async (resolvedShortlistId) => {
        try {
          const {
            type,
            id,
          } = categoryStore.extractPromotionContentFromObject(promotionStore.shortlists[resolvedShortlistId]);
          if (type === 'banner') {
            await promotionStore.fetchBanner({ id });
          }
        } catch (error) {
          if (error?.response?.status !== 404) {
            $logger.error(error);
          }
        }
        return resolvedShortlistId;
      });

      if (isReturningFromProductPage.value && isReturningEnabled.value) {
        productsStore.restoreFromProductListHistoryInformation();
      } else {
        // Restore the filters and sorting the `qq` parameter carries, the way the
        // category and boutique branches of AwPage do: this page is routed on its
        // own, so nothing else feeds the store from the URL and a reload would
        // otherwise render the unfiltered list under a filtered address.
        productsStore.setFilterFromQuery(route.query);
        await productsStore.fetchProducts({
          [route.query.shType === 'id' ? 'listId' : 'listName']: boutiqueId ? null : route.params.list_name,
          isCached: true,
          boutiqueId,
          itemsPerPage: productsStore.itemsPerPage || 16,
          page: 1,
          filterParams: productsStore.filterBy,
          sortKey: productsStore.sortKey || null,
          sortBy: productsStore.sortBy || null,
        });
      }

      const resolvedShortlistId = await shortlistReq;
      if (resolvedShortlistId) {
        await Promise.all([
          contentsStore.fetchStaticContentByResourceId({
            type: 'shortlist_content',
            resourceId: resolvedShortlistId,
          }),
          seoStore.fetchSeoData({
            seoModule: SEO_MODULE_SHORTLIST,
            id: resolvedShortlistId,
          }),
        ]);
      }
      return {
        shortlistId: resolvedShortlistId || null,
        notFound: !resolvedShortlistId,
      };
    } catch (error) {
      if (error?.response?.status !== 404) {
        $logger.error(error);
      }
      return {
        shortlistId: null,
        notFound: false,
      };
    }
  });

  // When the shortlist can't be resolved we redirect away. navigateTo with
  // `external` triggers a full document load on the client, so the rest of this
  // setup (computeds, onMounted side effects) is pointless and may even mutate
  // shared store state for a page that is being torn down — guard against that.
  const isNotFound = shortlistResult.value?.notFound ?? false;
  if (isNotFound) {
    const requestId = useAwRequestTracing().getFeServerRequestId();
    setCookieMsg({
      nuxtApp: useNuxtApp(),
      cb (redirectMsg) {
        redirectMsg[MSG_SHORTLIST_NOT_FOUND] = {
          requestId,
        };
      },
    });
    await navigateTo($localePath('/shop'), {
      external: true,
      redirectCode: 302,
    });
  }

  const { hasCategorySelectionFilter } = useCategorySelectionFilter();

  const shortlistId = computed(() => shortlistResult.value?.shortlistId ?? null);

  const getCategoryFromTree = computed(() => categoryStore.getCategoryFromTree);
  const shortlist = computed(() => promotionStore.shortlists[shortlistId.value] ?? undefined);
  const screenRange = computed(() => userInterfaceStore.mediaQueries);
  const itemsPerPage = computed(() => productsStore.itemsPerPage);
  const currentPage = computed(() => productsStore.currentPage);
  const itemCount = computed(() => productsStore.itemCount);
  const sortKey = computed(() => productsStore.sortKey);
  const sortBy = computed(() => productsStore.sortBy);
  const filterBy = computed(() => productsStore.filterBy);
  const products = computed(() => productsStore.renderedProducts);

  const productBaseQuery = computed(() => {
    const data = {
      isCached: true,
      listId: boutiqueId ? null : shortlistId.value,
      boutiqueId,
      itemsPerPage: itemsPerPage.value,
      page: currentPage.value,
      filterParams: filterBy.value,
    };
    if (sortKey.value) {
      data.sortKey = sortKey.value;
    }
    if (sortBy.value) {
      data.sortBy = sortBy.value;
    }
    return data;
  });

  async function loadMoreProductItems (loadingState) {
    isMoreLoadingCompleted.value = false;

    if (loadingState && itemCount.value === products.value.length) {
      loadingState.complete();
      return;
    }

    const result = await productsStore.fetchProducts({
      ...productBaseQuery.value,
      page: productBaseQuery.value.page + 1,
    });

    isMoreLoadingCompleted.value = true;
    if (loadingState) {
      if (!result || itemCount.value === products.value.length) {
        loadingState.complete();
      } else {
        loadingState.loaded();
      }
    }
  }

  async function orderProductItems (orderParams) {
    isMoreLoadingCompleted.value = false;
    let data = {
      ...productBaseQuery.value,
      itemsPerPage: currentPage.value * itemsPerPage.value,
      page: 1,
    };

    if (sortKey.value) {
      delete data.sortKey;
    }
    if (sortBy.value) {
      delete data.sortBy;
    }

    data = {
      ...data,
      ...orderParams.value,
    };

    await productsStore.fetchProducts(data);
    isMoreLoadingCompleted.value = true;
  }

  async function filterProductItems () {
    isMoreLoadingCompleted.value = false;
    await productsStore.fetchProducts({
      ...productBaseQuery.value,
      itemsPerPage: currentPage.value * itemsPerPage.value,
      page: 1,
    });
    isMoreLoadingCompleted.value = true;
  }

  watch(
    () => deliveryStore.setup,
    () => {
      refreshNuxtData();
    },
  );

  // Refetch when a filter is cleared from outside this page — the active-filter bar
  // (and the mobile filter modal) only bump this counter, they don't emit to us.
  // AwProductListPage and AwProductSearch watch it the same way.
  watch(
    () => productsStore.refreshProducts,
    () => {
      filterProductItems();
    },
  );

  onMounted(() => {
    if (isNotFound) {
      // The page is redirecting to /shop; don't touch shared store state.
      return;
    }
    categoryStore.setTreeId(treeId);
    if (isReturningFromProductPage.value) {
      const scrollToProductId = createScrollToProductId();
      const productId = productsStore.productListHistoryInformation?.productId;
      scrollToProductId(productId);
    }
  });
</script>

<style module lang="scss" rel="stylesheet/scss">
.promotion {
  @include lg() {
    margin-bottom: 27px;
  }
  margin: 23px 0 18px;
}

.headline {
  margin-top: 12px;
  margin-bottom: 20px;
}
</style>
